// Joana chat shell — the three-pane frame (the design canvas's Shell page:
// `docs/design/canvas/Main.dc.html`, `Rail.dc.html`, `Empty.dc.html`).
//
// ## Why this is its own module
//
// `views.ts` is 3600 lines and the frame is the one thing every screen is
// inside, so growing it there would have buried the layout in the middle of the
// screens it lays out. The frame knows nothing about any screen: it is handed a
// LIST node and a DETAIL node and arranges them, which is also what makes it
// testable without building a chat.
//
// ## What it fixes
//
// The web client had no navigation structure at all: one full-width column per
// screen, a conversation REPLACED the list, and five controls had accumulated
// in the header because there was nowhere else to put them. On a 1440px screen
// that is one column of chat and two thirds of nothing. The rail is the
// somewhere else.
//
// ## The rail's rule (Rail.dc.html, and it is the reason the canvas exists)
//
// Three zones. Zone 1 is the surfaces the phones show as tabs and NEVER grows —
// a fifth entry here has to become a fifth tab on a 390px phone too, which is
// an ADR and not a design tweak. Zone 2 is the desktop promotion, capped at
// four. Zone 3 is you, pinned to the bottom. A new area enters at zone 2 or it
// enters More; it never enters the list-column header.
//
// ## Two deviations from the artboards, both deliberate
//
//   * The canvas draws SVG glyphs. The catalog has no SVG primitive and adding
//     one is a change to the surface untrusted mini-apps also render through,
//     so a rail slot is a text glyph with its NAME under it. An `<img>` holding
//     a data-URI SVG was the other candidate and is worse: the stroke colour
//     would be baked into the URL, and the appearance switches in CSS with no
//     re-render, so every icon would have kept its light-mode colour in dark.
//   * Wallet and Calls are drawn on the canvas and are NOT here. Neither screen
//     exists on the web, and a rail slot that opens nothing teaches people the
//     surface is broken — the same rule that kept the search control off the
//     header until there was a directory behind it (ADR-0024 A16).

import type { VAction, VNode } from "../render/catalog.ts";
import { chatById, type ShellState } from "./store.ts";

/** The translator `views.ts` resolves for the pass. Passed in rather than
 *  imported so the frame has no opinion about the language. */
export type Translate = (key: string, ...args: (string | number)[]) => string;

// --- geometry (lifted from views.ts, so nothing here is invented) -----------

/** The rail. Already at its minimum: it never narrows, it only moves. */
export const RAIL_W = 72;
/** The list column at its comfortable width. */
export const LIST_W = 400;
/** …and the floor it narrows to before the detail pane starts losing room. */
export const LIST_MIN_W = 320;
/** Three panes at or above this width. */
export const WIDE_MIN = 1180;
/** Two panes down to here; below it, one pane and a bottom bar. */
export const MEDIUM_MIN = 760;

export type Layout = "wide" | "medium" | "narrow";

/** Which of the three shapes a window of this width gets (Empty.dc.html). */
export function layoutFor(width: number): Layout {
  if (width >= WIDE_MIN) return "wide";
  if (width >= MEDIUM_MIN) return "medium";
  return "narrow";
}

/**
 * A rail destination.
 *
 * Not the same list as `Screen`: three Office screens are one area, a
 * conversation is part of Chats, and login is not an area at all. The rail
 * highlights an AREA, which is why a drill-down inside Office does not make the
 * rail look like it lost its place.
 */
export type Area = "chats" | "updates" | "discover" | "office" | "apps" | "me";

export function areaOf(state: ShellState): Area {
  // ADR-0018 — a zone IS a chat, so it opens the conversation screen, but it
  // is never in Chats: lighting Chats would point at a list it is not on.
  // Where you reach a zone from is Office, so Office stays lit.
  if (state.screen === "conversation" && chatById(state, state.activeChatId)?.chat_type === "zone") {
    return "office";
  }
  switch (state.screen) {
    case "updates": return "updates";
    case "explore": return "discover";
    case "office":
    case "office_new":
    case "office_publisher":
    // ADR-0018 — organisations live INSIDE Office, so drilling into one must
    // not make the rail look like it lost its place.
    case "office_orgs":
    case "office_org":
    case "office_org_new":
    case "office_zone_new": return "office";
    case "apps": return "apps";
    case "profile": return "me";
    default: return "chats";
  }
}

/** A shell interaction, same shape as `views.ts`'s `act`. */
function act(cmd: string, extra: Record<string, unknown> = {}): VAction {
  return { intent: "shell", payload: { cmd, ...extra } };
}

const C = {
  rail: "surface.variant",
  surface: "surface",
  outline: "outline.variant",
  accent: "accent",
  textSecondary: "text.secondary",
  textPrimary: "text.primary",
  badge: "brand.gradient.end",
  onBadge: "brand.on.bar",
  canvas: "chat.canvas",
};

/**
 * A rail entry. The label is a FUNCTION of the translator and not a key
 * string, so `t("Chats")` appears literally in this file — `scripts/i18n-check`
 * reads source for `t("…")` calls and a `t(entry.key)` is invisible to it, so
 * a slot added with an untranslated name would have shipped English behind a
 * green ratchet.
 */
interface RailEntry { area: Area; glyph: string; label: (t: Translate) => string }

/** Zone 1 — the phones' tabs, in the phones' order. Muscle memory is the point. */
const ZONE_1: RailEntry[] = [
  { area: "chats", glyph: "✉", label: (t) => t("Chats") },
  { area: "updates", glyph: "◍", label: (t) => t("Updates") },
  { area: "discover", glyph: "◈", label: (t) => t("Discover") },
];

/** Zone 2 — behind More on a phone, a permanent slot on a desktop. Cap: four. */
const ZONE_2: RailEntry[] = [
  { area: "office", glyph: "▤", label: (t) => t("Office") },
  { area: "apps", glyph: "⊞", label: (t) => t("Mini-apps") },
];

/**
 * How many conversations are waiting — the ONE count in the rail.
 *
 * Conversations and not messages, which is what a person acts on: eleven
 * messages in one thread is one thing to open. ADR-0023's refusal of tallies
 * covers Updates and Discover, and those two carry a dot or nothing.
 */
export function waitingChats(state: ShellState): number {
  return state.chats.filter((c) => (c.unread_count ?? 0) > 0).length;
}

/** Is there a status this viewer has not seen? The dot's whole vocabulary. */
export function unseenUpdates(state: ShellState): boolean {
  const me = state.me?.id ?? null;
  return state.statusTray.some((e) => !e.all_viewed && e.author_id !== me);
}

interface SlotOpts {
  active: boolean;
  count?: number;
  dot?: boolean;
  /** Row layout (the narrow bottom bar) rather than a column tile. */
  row?: boolean;
}

function slot(area: Area, glyph: string, label: string, opts: SlotOpts): VNode {
  const tint = opts.active ? C.accent : C.textSecondary;
  const children: VNode[] = [
    {
      type: "text",
      id: `rail-${area}-glyph`,
      props: { value: glyph },
      style: { font_size: 19, color: tint, line_height: 1.1, text_align: "center" },
    },
    {
      type: "text",
      id: `rail-${area}-label`,
      props: { value: label },
      // A column tile WRAPS: nowrap clipped Kiswahili's "Programu ndogo" to
      // "rogramu ndog" in the 54px a tile leaves. Two short lines read; a
      // clipped word does not. The bottom bar's row stays one line.
      style: {
        font_size: 10, font_weight: 600, color: tint, text_align: "center",
        ...(opts.row ? { white_space: "nowrap" } : { white_space: "normal", max_width: 54, line_height: 1.15 }),
      },
    },
  ];
  // Badge grammar (Rail.dc.html): a count for Chats and Calls, a dot for the
  // surfaces that refuse tallies, and nothing is the default.
  if (opts.count && opts.count > 0) {
    children.push({
      type: "text",
      id: `rail-${area}-count`,
      props: { value: String(opts.count) },
      style: {
        position: "absolute", top: 1, right: 6,
        min_width: 17, radius: 999, padding: "1 5",
        background: C.badge, color: C.onBadge,
        font_size: 10, font_weight: 700, text_align: "center",
      },
    });
  } else if (opts.dot) {
    children.push({
      type: "text",
      id: `rail-${area}-dot`,
      props: { value: "●" },
      style: { position: "absolute", top: 2, right: 12, font_size: 9, color: C.badge },
    });
  }
  return {
    type: "stack",
    id: `rail-${area}`,
    style: {
      direction: "column", align: "center", justify: "center", gap: 1,
      position: "relative",
      // The active state is a raised tile and an accent glyph — never a
      // coloured fill, which would compete with the badge sitting on it.
      background: opts.active ? C.surface : "transparent",
      radius: 12,
      padding: "6 2",
      ...(opts.row ? { flex: 1 } : { width: 58 }),
    },
    action: act("rail_open", { area }),
    children,
  };
}

/**
 * The three-bubble mark, at the top of the rail.
 *
 * The REAL logo and not a glyph standing in for it: `app/favicon.svg` is the
 * brand master the build already ships, it is same-origin (so `safeImageUrl`
 * passes it), and it carries its own gradient — which is what a hand-drawn
 * substitute could not, because the catalog has no SVG primitive and a data
 * URI would have baked one appearance's colours into the URL.
 */
function brandMark(size: number, id: string, margin: string): VNode {
  return {
    type: "image",
    id,
    props: { url: "/favicon.svg", alt: "" },
    style: { width: size, height: size, radius: Math.round(size * 0.31), margin },
  };
}

function slotsFor(state: ShellState, t: Translate, row: boolean): { one: VNode[]; two: VNode[] } {
  const area = areaOf(state);
  const unseen = unseenUpdates(state);
  const one = ZONE_1.map((e) =>
    slot(e.area, e.area === "updates" && !unseen ? "◌" : e.glyph, e.label(t), {
      active: area === e.area,
      count: e.area === "chats" ? waitingChats(state) : undefined,
      dot: e.area === "updates" ? unseen : false,
      row,
    })
  );
  const two = ZONE_2.map((e) => slot(e.area, e.glyph, e.label(t), { active: area === e.area, row }));
  return { one, two };
}

/**
 * Zone 3 — you.
 *
 * The canvas draws two entries here, settings and the avatar. The web has one
 * screen behind both: `profileView` IS the settings screen. Two controls that
 * open the same place is a worse answer than one, so this is one — and the
 * canvas's "exactly two, forever" is a cap, not a quota.
 */
function meSlot(state: ShellState, t: Translate, avatar: VNode, row: boolean): VNode {
  const active = areaOf(state) === "me";
  return {
    type: "stack",
    id: "rail-me",
    style: {
      direction: "column", align: "center", justify: "center", gap: 3,
      radius: 12, padding: "6 2",
      background: active ? C.surface : "transparent",
      ...(row ? { flex: 1 } : { width: 58 }),
    },
    action: act("rail_open", { area: "me" }),
    children: [
      avatar,
      {
        type: "text", id: "rail-me-label", props: { value: t("Profile") },
        style: {
          font_size: 10, font_weight: 600, text_align: "center", white_space: "nowrap",
          color: active ? C.accent : C.textSecondary,
        },
      },
    ],
  };
}

/** The vertical rail (wide + medium). */
function railColumn(state: ShellState, t: Translate, avatar: VNode): VNode {
  const { one, two } = slotsFor(state, t, false);
  return {
    type: "stack",
    id: "rail",
    style: {
      direction: "column", align: "center", gap: 2,
      width: RAIL_W, flex_shrink: 0, padding: "12 0 10 0",
      background: C.rail, border_right: "1px solid outline.variant",
      overflow: "hidden",
    },
    children: [
      brandMark(34, "rail-brand", "0 0 10 0"),
      ...one,
      { type: "divider", id: "rail-zone-split", style: { width: 26, height: 1, background: C.outline, margin: "8 0" } },
      ...two,
      { type: "spacer", id: "rail-gap", style: { flex: 1 } },
      meSlot(state, t, avatar, false),
    ],
  };
}

/** The bottom bar (narrow) — the shape the phones already ship. */
function railBar(state: ShellState, t: Translate, avatar: VNode): VNode {
  const { one, two } = slotsFor(state, t, true);
  return {
    type: "stack",
    id: "rail",
    style: {
      direction: "row", align: "stretch", justify: "space-between",
      padding: "4 2", background: C.rail, border: "1px solid outline.variant",
      flex_shrink: 0,
    },
    children: [...one, ...two, meSlot(state, t, avatar, true)],
  };
}

/**
 * The empty detail pane (Empty.dc.html).
 *
 * WhatsApp Web fills this space with the thing it most wants you to try. Ours
 * states the one fact a linked browser is uniquely placed to state: this
 * device is linked, and it is an encryption device of its own (ADR-0027 D4).
 *
 * It says that ONLY when `signalDeviceLinked` is true, and it says nothing at
 * all about whether the messages themselves are end-to-end encrypted — that is
 * a per-chat fact this pane cannot see, and a pane that claimed it here would
 * be the s150 banner again, in a bigger typeface.
 */
function emptyDetail(state: ShellState, t: Translate): VNode {
  const linked = state.signalDeviceLinked === true;
  const children: VNode[] = [
    brandMark(84, "detail-empty-mark", "0"),
    {
      type: "text", id: "detail-empty-title",
      props: { value: t("WaveTalk on your computer") },
      style: { font_size: 21, font_weight: 700, color: C.textPrimary, text_align: "center" },
    },
    {
      type: "text", id: "detail-empty-body",
      props: { value: t("Choose a conversation to start.") },
      style: { font_size: 14, color: C.textSecondary, text_align: "center", max_width: 420, line_height: 1.55 },
    },
  ];
  if (linked) {
    children.push({
      type: "text", id: "detail-empty-linked",
      props: { value: t("🔗 Linked device") },
      style: {
        font_size: 12.5, color: C.textSecondary, text_align: "center",
        background: "accent.wash", radius: 999, padding: "6 13",
      },
    });
  }
  return {
    type: "stack",
    id: "detail-empty",
    style: {
      direction: "column", align: "center", justify: "center", gap: 16,
      flex: 1, padding: 30, background: C.canvas,
    },
    children,
  };
}

export interface FrameParts {
  state: ShellState;
  t: Translate;
  layout: Layout;
  /** The area's list column — today's full screen, unchanged. */
  list: VNode;
  /** What the right-hand pane shows: the current area's detail if it has one,
   *  otherwise the open conversation. Null draws the empty pane. */
  detail: VNode | null;
  /**
   * Does `detail` belong to the area the rail is highlighting?
   *
   * Only narrow reads it, and only narrow needs it. There is ONE pane there,
   * so the detail may take it when it is the area's own — a channel, a
   * publisher, a thread — and must not when it is a conversation left open in
   * the background, which would fill the screen while the rail said Updates.
   */
  detailIsCurrentArea?: boolean;
  /** The rail's own avatar node (built by `views.ts`, which owns the loader). */
  avatar: VNode;
}

/**
 * Wrap an area in the frame.
 *
 * Narrow shows ONE pane and the rail moves to the bottom — which is the phone
 * layout the native apps already ship, so below 760px the design converges
 * rather than inventing a third shape.
 */
export function frame(parts: FrameParts): VNode {
  const { state, t, layout, list, detail, avatar } = parts;

  if (layout === "narrow") {
    // One pane: the area's own detail when it has one open, the conversation
    // when that is the screen, otherwise the area's list. The middle case is
    // why `screen` is asked rather than `detail != null` — a conversation can
    // be open while you are reading Discover, and on one pane that is the
    // rail's word against the screen's.
    const showsDetail = parts.detailIsCurrentArea === true || state.screen === "conversation";
    const pane = showsDetail && detail ? detail : list;
    return {
      type: "stack",
      id: "shell-frame",
      style: { direction: "column", height: "100vh", width: "100%", background: C.surface },
      children: [
        { type: "stack", id: "shell-pane", style: { direction: "column", flex: 1, min_height: 0, overflow: "hidden" }, children: [pane] },
        railBar(state, t, avatar),
      ],
    };
  }

  const listWidth = layout === "wide" ? LIST_W : LIST_MIN_W;
  return {
    type: "stack",
    id: "shell-frame",
    style: { direction: "row", height: "100vh", width: "100%", background: C.surface },
    children: [
      railColumn(state, t, avatar),
      {
        type: "stack",
        id: "shell-list",
        style: {
          direction: "column", width: listWidth, flex_shrink: 0, min_width: 0,
          border_right: "1px solid outline.variant", overflow: "hidden",
        },
        children: [list],
      },
      {
        type: "stack",
        id: "shell-detail",
        style: { direction: "column", flex: 1, min_width: 0, overflow: "hidden" },
        children: [detail ?? emptyDetail(state, t)],
      },
    ],
  };
}
